import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const BusinessesEdit = ({ business = null, onSuccess = () => {}, onCancel = () => {} }) => {
  const [name, setName] = useState(business?.name ?? "");
  const [email, setEmail] = useState(business?.email ?? "");
  const [phone, setPhone] = useState(business?.phone ?? "");
  const [address, setAddress] = useState(business?.address ?? "");
  const [profit, setProfit] = useState(business?.profit_share_percentage ?? "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    setName(business?.name ?? "");
    setEmail(business?.email ?? "");
    setPhone(business?.phone ?? "");
    setAddress(business?.address ?? "");
    setProfit(business?.profit_share_percentage ?? "");
    setPassword("");
  }, [business]);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const validate = () => {
    if (!business) {
      setSnack({ open: true, message: "No business selected", severity: "error" });
      return false;
    }
    if (!name.trim()) {
      setSnack({ open: true, message: "Name is required", severity: "error" });
      return false;
    }
    if (!email.trim()) {
      setSnack({ open: true, message: "Email is required", severity: "error" });
      return false;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      setSnack({ open: true, message: "Enter a valid email", severity: "error" });
      return false;
    }
    if (!phone.trim()) {
      setSnack({ open: true, message: "Phone is required", severity: "error" });
      return false;
    }
    if (!/^\d{7,15}$/.test(phone.replace(/\D/g, ""))) {
      setSnack({ open: true, message: "Enter a valid phone (7-15 digits)", severity: "error" });
      return false;
    }
    if (!profit.toString().trim()) {
      setSnack({ open: true, message: "Profit share percentage is required", severity: "error" });
      return false;
    }
    if (isNaN(profit) || Number(profit) < 0) {
      setSnack({ open: true, message: "Enter a valid profit percentage", severity: "error" });
      return false;
    }
    // Password is required for edit as per your requirement
    if (!password) {
      setSnack({ open: true, message: "Password is required to update business", severity: "error" });
      return false;
    }
    if (password.length < 6) {
      setSnack({ open: true, message: "Password must be at least 6 characters", severity: "error" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        id: business.id,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: address.trim(),
        profit_share_percentage: Number(profit).toFixed(2).toString(),
        password,
      };

      const res = await ApiServices.put(`external-businesses/${business.id}/`, payload);
      const data = res?.data ?? res;

      setSnack({ open: true, message: "Business updated successfully", severity: "success" });
      onSuccess("Business updated successfully", data);
    } catch (err) {
      console.error(err);
      let msg = "Failed to update business.";
      if (err?.response?.data) {
        const d = err.response.data;
        if (typeof d === "string") msg = d;
        else if (d?.detail) msg = d.detail;
        else msg = Object.values(d).flat().join(" ") || msg;
      } else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      <Typography sx={{ color: "#094c9f", fontWeight: 700 }}>Edit Business</Typography>

      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
      <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
      <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} required fullWidth />
      <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} multiline rows={2} fullWidth />
      <TextField
        label="Profit share percentage"
        value={profit}
        onChange={(e) => setProfit(e.target.value)}
        required
        type="number"
        inputProps={{ step: "0.01", min: 0 }}
        helperText="Enter number like 20 or 15.5"
        fullWidth
      />
      <TextField
        label="Password (required)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        type="password"
        helperText="Enter password to confirm edit"
        fullWidth
      />

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: "#094c9f" }}>
          {loading ? <CircularProgress size={20} /> : "Save Changes"}
        </Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessesEdit;
